import { Button } from "@/components/ui/baseUi/button"
import { HustleupSmallIcon } from "@images/svgIcons/hustleUpSmallIcon"
import { word } from "@modules/core/utils/i18n"
import { CurrencyFormattedPrice } from "@modules/currency/components/currencyUtils"
import { ProgramBundleType } from "@modules/programBundle/api/programBundleDto"
import {
  ProgramBundleNoUnfollowModal,
  type ProgramBundleNoUnfollowModalRef,
} from "@modules/programBundle/components/programBundleNoUnfollowModal"
import {
  ProgramBundlePauseOnOffModal,
  type ProgramBundlePauseOnOffModalRef,
} from "@modules/programBundle/components/programBundlePauseOnOffModal"
import { ProgramBundleLightEntity } from "@modules/programBundle/entities/programBundleEntity"
import { useProgramBundleSidebar } from "@modules/programBundle/hooks/useProgramBundleSidebar"
import { useProgramBundleSubscription } from "@modules/programBundle/hooks/useProgramBundleSubscription"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramOnOffProgression } from "@modules/programs/hooks/useProgramsOnOff"
import { ProgramProgressionStatus } from "@modules/programs/types/programOnOffTypes"
import { ProgramSubscriptionStatus, ProgramType } from "@modules/programs/types/programTypes"
import { LoaderCircle } from "lucide-react"
import { useMemo, useRef } from "react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramBundlePriceInProgramDetailsCardProps = {
  programId: string
  programBundleLight: ProgramBundleLightEntity
  isSubscribedInBothProgramAndBundle: boolean
} & React.ComponentProps<"div">

export function ProgramBundlePriceInProgramDetailsCard({
  programId,
  programBundleLight,
  isSubscribedInBothProgramAndBundle,
  ...props
}: ProgramBundlePriceInProgramDetailsCardProps) {
  const programBundleNoUnfollowModalRef = useRef<ProgramBundleNoUnfollowModalRef>(null)
  const programBundlePauseOnOffModalRef = useRef<ProgramBundlePauseOnOffModalRef>(null)

  const { data: program } = useProgram(programId)
  const { navigateToProgramBundleResume } = useProgramBundleSidebar()
  const {
    data: programBundleSubscriber,
    isLoading,
    followProgamsInProgramBundle,
    unFollowProgamsInProgramBundle,
    isPending,
  } = useProgramBundleSubscription(programBundleLight.programBundleId)

  const { data: progression } = useProgramOnOffProgression(
    programId,
    program.type !== ProgramType.ONOFF ||
      !(
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION
      ),
  )

  const canUserFollowProgram = useMemo(
    () => programBundleSubscriber?.canUserFollowBundlePrograms(),
    [programBundleSubscriber],
  )
  const isFollowed = useMemo(
    () =>
      programBundleSubscriber?.programBundle?.programs &&
      programBundleSubscriber.programBundle.programs
        .filter((p) => p.followedInBundle)
        .map((p) => p.id)
        .includes(programId),
    [programBundleSubscriber],
  )

  const toggleFollow = async () => {
    if (isSubscribedInBothProgramAndBundle) {
      // Tell the user that he can't unfollow the program because he is subscribed to it individually
      programBundleNoUnfollowModalRef.current?.open()
    } else if (
      isFollowed &&
      program.type === ProgramType.ONOFF &&
      progression &&
      progression.status === ProgramProgressionStatus.STARTED
    ) {
      // Tell the user that his progression will be paused if he unfollows the program
      programBundlePauseOnOffModalRef.current?.open()
    } else if (isFollowed) {
      // Unfollow the program
      await unFollowProgamsInProgramBundle([programId])
    } else {
      // Follow the program
      await followProgamsInProgramBundle([programId])
    }
  }

  if (!programBundleLight) return null

  return (
    <div {...props}>
      <div style={{ marginBottom: 7 }}>
        <Text color="currentColor" className="text-textDefault-caption" fontStyle="TS">
          {programBundleLight.type === ProgramBundleType.VIRTUAL
            ? word("program.subscription.label")
            : word("program.price.label")}
        </Text>
      </div>

      <div className="card relative flex flex-col gap-2 overflow-hidden rounded-4xl">
        <Text fontStyle="TM">{programBundleLight.programBundleName}</Text>

        <div className="z-2 flex w-full flex-col gap-2">
          {programBundleLight.paidPrice?.value ? (
            programBundleLight.type === ProgramBundleType.VIRTUAL ? (
              <div className="flex flex-row items-baseline gap-2">
                <CurrencyFormattedPrice price={programBundleLight.paidPrice} />
                <div className="flex">
                  <Text fontStyle="LL">{word("program.price.pricePerMonth")}</Text>
                </div>
              </div>
            ) : (
              <>
                <Text fontStyle="TM">{word("program.price.singlePurchase")}</Text>
                <div className="flex flex-row items-baseline gap-2">
                  <CurrencyFormattedPrice price={programBundleLight.paidPrice} />
                  <div className="flex">
                    <Text fontStyle="LL">{word("program.price.onlyOnce")}</Text>
                  </div>
                </div>
              </>
            )
          ) : (
            <>
              <Text fontStyle="TM">{word("program.price.free")}</Text>
              <Text fontStyle="BM">{word("program.price.freeForAll")}</Text>
            </>
          )}
        </div>

        <Text fontStyle="BM" className="mb-2">
          {word("programBundle.details.programPartOfBundle", { bundleName: programBundleLight.programBundleName })}
        </Text>

        <div className="z-2 flex flex-row items-center justify-end gap-8">
          {!isLoading && canUserFollowProgram && (
            <Button isLoading={isPending} variant="outline" onClick={toggleFollow} disabled={isLoading || isPending}>
              {isLoading || (isPending && <LoaderCircle className="animate-spin" />)}
              {isFollowed ? word("programBundle.follow.remove.buttonAlt") : word("programBundle.follow.add.button")}
            </Button>
          )}

          <Button
            variant="primary"
            onClick={() =>
              navigateToProgramBundleResume(programBundleLight.programBundleId, { replaceAllComponents: true })
            }
          >
            {word("programBundle.details.backToProgramBundle")}
          </Button>
        </div>

        <HustleupSmallIcon
          className="absolute top-0 right-[-40px] h-full bg-contain opacity-50"
          color="var(--surface-background)"
        />

        <ProgramBundleNoUnfollowModal ref={programBundleNoUnfollowModalRef} programType={program.type} />
        <ProgramBundlePauseOnOffModal
          ref={programBundlePauseOnOffModalRef}
          onConfirmUnfollow={() => unFollowProgamsInProgramBundle([programId])}
        />
      </div>
    </div>
  )
}
